import { appIntl } from '@components/locale/IntlGlobalProvider';
import {
  addDays,
  addHours,
  addMinutes,
  addWeeks,
  Duration,
  endOfMonth,
  endOfQuarter,
  endOfYear,
  format,
  FormatDistanceToken,
  formatDuration as formatDurationFns,
  intervalToDuration,
  startOfMonth,
  startOfQuarter,
  startOfYear,
  subDays,
  subHours,
  subMinutes,
  subMonths,
  subQuarters,
  subWeeks,
  subYears,
} from 'date-fns';

import {
  AnyDate,
  DateTimeEnum,
  DateTimeRange,
  DateTimeRangePreset,
  ISO_WITH_TIMEZONE,
  TimeReference,
} from '@/types/dateTime.types';
import { ReplayTimeScaleEnum, ReplayTimeScaleToTimelineTimeAxis } from '@/types/replay/replay.types';

export function timeDiff(date1: AnyDate, date2?: AnyDate | null): number {
  const d1 = new Date(date1).getTime();
  const d2 = date2 ? new Date(date2).getTime() : Date.now();
  return d1 - d2;
}

export function timeDuration(date1: AnyDate, date2?: AnyDate | null): number {
  return Math.abs(timeDiff(date1, date2));
}

export function compareDay(date1: AnyDate, date2: AnyDate): boolean {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  return d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDay() === d2.getDay();
}

export function sortDate(date1: AnyDate, date2: AnyDate): number {
  const d1 = new Date(date1).getTime();
  const d2 = new Date(date2).getTime();
  return d2 - d1;
}

export function millisToDateTimeString(millis: number): string {
  const day = Math.floor(millis / (1000 * 60 * 60 * 24));
  const hr = Math.floor(millis / (1000 * 60 * 60)) % 24;
  const min = Math.floor(millis / (1000 * 60)) % 60;
  const sec = Math.floor(millis / 1000) % 60;

  const days = day < 10 ? `0${day}` : day;
  const hours = hr < 10 ? `0${hr}` : hr;
  const minutes = min < 10 ? `0${min}` : min;
  const seconds = sec < 10 ? `0${sec}` : sec;

  return `${days}:${hours}:${minutes}:${seconds}`;
}

export function minutesToTime(minutes: number) {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return [hours, remainingMinutes];
}

export function formatTimeTwoDigits(value: string) {
  return value.length === 1 ? `0${value}` : value;
}

export function getFormat(type: DateTimeEnum) {
  switch (type) {
    case DateTimeEnum.DATE_TIME_SECONDS:
      return "dd/MM/yyyy - HH'h'mm'm'ss";
    case DateTimeEnum.DATE_TIME:
      return "dd/MM/yyyy - HH'h'mm";
    case DateTimeEnum.TIME_DATE:
      return 'HH:mm:ss - dd/MM/yyyy';
    case DateTimeEnum.TIME:
      return 'HH:mm';
    case DateTimeEnum.TIME_SECONDS:
      return 'HH:mm:ss';
    case DateTimeEnum.DATE_TIMELINE:
      return 'DD/MM';
    case DateTimeEnum.DATE_FULL:
      return 'dd/MM/yyyy';
    case DateTimeEnum.DATE_TIME_SECONDS_ALTERNATE:
      return "yyyy-MM-dd_HH'h'mm'm'ss";
    case DateTimeEnum.DATE_TIME_SECONDS_PERIM_FORMAT:
      return "yyyy-MM-dd'T'HH:mm:ss";
    case DateTimeEnum.DATE:
    default:
      return 'dd/MM/yy';
  }
}

export function formatDate(date: AnyDate, type: DateTimeEnum) {
  return format(new Date(date), getFormat(type));
}

export function formatDuration(
  duration: Duration,
  format: (keyof Duration)[] = ['days', 'hours', 'minutes', 'seconds'],
) {
  const formatDistance = {
    xYears: appIntl().formatMessage({ id: 'common.format.xYears' }),
    xMonths: appIntl().formatMessage({ id: 'common.format.xMonths' }),
    xWeeks: appIntl().formatMessage({ id: 'common.format.xWeeks' }),
    xDays: appIntl().formatMessage({ id: 'common.format.xDays' }),
    xHours: appIntl().formatMessage({ id: 'common.format.xHours' }),
    xMinutes: appIntl().formatMessage({ id: 'common.format.xMinutes' }),
    xSeconds: appIntl().formatMessage({ id: 'common.format.xSeconds' }),
  } as Partial<Record<FormatDistanceToken, string>>;

  return formatDurationFns(duration, {
    format,
    locale: {
      formatDistance: (token: FormatDistanceToken, count: number) =>
        formatDistance[token] ? formatDistance[token].replace('{{count}}', `${count}`) : `${count}`,
    },
  });
}

export function formatDurationFromTo(
  start: AnyDate,
  end: AnyDate,
  format: (keyof Duration)[] = ['days', 'hours', 'minutes', 'seconds'],
) {
  if (timeDuration(start, end) < 1000) {
    return '0s';
  }
  const duration: Duration = intervalToDuration({ start: new Date(start), end: new Date(end) });
  return formatDuration(duration, format);
}

export function formatDurationFromMillis(
  millis: number,
  format: TimeReference[] = ['days', 'hours', 'minutes', 'seconds'],
) {
  if (millis < 0) {
    return '';
  } else if (millis < 1000) {
    return '0s';
  }

  const seconds = Math.floor(millis / 1000) % 60;
  const minutes = Math.floor(millis / (1000 * 60)) % 60;
  const hours = Math.floor(millis / (1000 * 60 * 60)) % 24;
  const days = Math.floor(millis / (1000 * 60 * 60 * 24));

  const duration: Duration = { days, hours, minutes, seconds };
  return formatDuration(duration, format);
}

export function customAddSubForDate(date: Date, action: 'add' | 'sub', timeScale: ReplayTimeScaleEnum, factor: number) {
  const { scale, step } = ReplayTimeScaleToTimelineTimeAxis[timeScale];
  const amount = step * factor;
  let addSubFunction: (date: Date, amount: number) => Date;
  switch (scale) {
    case 'minute':
      addSubFunction = action === 'add' ? addMinutes : subMinutes;
      break;
    case 'hour':
      addSubFunction = action === 'add' ? addHours : subHours;
      break;
    case 'day':
      addSubFunction = action === 'add' ? addDays : subDays;
      break;
    case 'week':
      addSubFunction = action === 'add' ? addWeeks : subWeeks;
      break;
  }
  return addSubFunction(date, amount);
}

export function convertDuration(duration: number, srcReference: TimeReference, targetReference: TimeReference) {
  const getTimeFactorToSeconds = (reference: TimeReference) => {
    switch (reference) {
      case 'days':
        return 60 * 60 * 24;
      case 'hours': {
        return 60 * 60;
      }
      case 'minutes': {
        return 60;
      }
      case 'seconds': {
        return 1;
      }
    }
  };

  const durationInSeconds = Math.floor(duration * getTimeFactorToSeconds(srcReference));
  return Math.round((durationInSeconds / getTimeFactorToSeconds(targetReference)) * 100) / 100;
}

export function getDateTimeRangeFromPreset(dateTimeRangePreset: DateTimeRangePreset, now = new Date()): DateTimeRange {
  switch (dateTimeRangePreset) {
    case 'LAST_24_HOURS':
      return { from: subDays(now, 1), to: now };
    case 'ROLLING_WEEK':
      return { from: subDays(now, 7), to: now };
    case 'LAST_MONTH':
      return { from: startOfMonth(subMonths(now, 1)), to: endOfMonth(subMonths(now, 1)) };
    case 'ROLLING_MONTH':
      return {
        from: subMonths(now, 1),
        to: now,
      };
    case 'LAST_QUARTER':
      return {
        from: startOfQuarter(subQuarters(now, 1)),
        to: endOfQuarter(subQuarters(now, 1)),
      };
    case 'ROLLING_QUARTER':
      return {
        from: subMonths(now, 3),
        to: now,
      };
    case 'LAST_YEAR':
      return {
        from: startOfYear(subYears(now, 1)),
        to: endOfYear(subYears(now, 1)),
      };
    case 'ROLLING_YEAR':
      return { from: subYears(now, 1), to: now };
    case 'CUSTOM':
      throw new Error('Custom period should be handled outside');
  }
}

export function toISOStringWithTimeZone(date: Date) {
  return format(date, ISO_WITH_TIMEZONE);
}
